
import {throwError as observableThrowError,  Observable } from 'rxjs';

import {catchError,  retryWhen, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';

import { BaseEntity, SentidoOrdenacao, EntityPagedResult, BnDateUtils } from '@bndes/bndes-angular-utils'

import { HelperService } from './helper.service';
import { LoggerService } from './logger.service';
import { ErrorHandlerService } from './error-handler.service';


import { BaseEntityService, progressEventErrorRetryStrategy } from '@smdapp/shared/services/base-entity.service';
import { blockUi } from '@smdapp/ng-shared/components/loading-overlay/loading-interceptor';

@Injectable()
export abstract class AppBackEndService<T extends BaseEntity> extends BaseEntityService<T> {

  constructor(
    protected http: HttpClient,
    protected helperService: HelperService,
    protected logger: LoggerService,
    protected errorHandlerService: ErrorHandlerService) {
    super(http);
  }

  public create(options?: {}, pathParams?:{}): Observable<T>
  {
    return super.create(options, pathParams).pipe(catchError((e) => this.handleError(e)));
  }

  public findById(id: number, options?: {}, pathParams?:{}): Observable<T>
  {
    return super.findById(id, options, pathParams).pipe(catchError((e) => this.handleError(e)));
  }

  public find(params?: any, options?: any, pathParams?:{}): Observable<T[]>
  {
    return super.find(params, options, pathParams).pipe(catchError((e) => this.handleError(e)));
  }

  public findAll(options?: any, pathParams?:{}): Observable<T[]>
  {
    return super.findAll(options, pathParams).pipe(catchError((e) => this.handleError(e)));
  }

  public findPaged(numeroPagina: number = 1, tamanhoPagina: number = 20, params?: any,  options?: any, pathParams?:{}): Observable<EntityPagedResult<T>>
  {
      return super.findPaged(numeroPagina, tamanhoPagina, params, options, pathParams).pipe(catchError((e) => this.handleError(e)));
  }

  public findOrderedAndPaged(numeroPagina: number = 1, tamanhoPagina: number = 20, parametroOrdenacao?: string, sentidoOrdenacao?: SentidoOrdenacao, params?: any, options?: any, pathParams?:{}): Observable<EntityPagedResult<T>>
  {
      return super.findOrderedAndPaged(numeroPagina, tamanhoPagina, parametroOrdenacao, sentidoOrdenacao, params, options, pathParams).pipe(catchError((e) => this.handleError(e)));
  }

  public save(entity: T, options?: any, pathParams?:{}): Observable<T>
  {
    return super.save(entity, options, pathParams).pipe(
      catchError((e) => this.handleError(e)));
  }

  public update(entity: T, options?: any, pathParams?:{}): Observable<T>
  {
    return this.http.put(`${this.getBaseURL(pathParams)}/${entity.id}`, entity, blockUi()).pipe(map(response => this.convertToType(response)),catchError((e) => this.handleError(e)),);
  }

  public remove(id: number, options?: any, pathParams?:{}): Observable<Object>
  {
    return super.remove(id, options, pathParams).pipe(catchError((e) => this.handleError(e)));
  }

  public ative(entity: T, pathParams?:{}): Observable<T>
  {
    return this.http.put(`${this.getBaseURL(pathParams)}/${entity.id}/ativar`, null, blockUi()).pipe(
          map(response => this.convertToType(response)),
          catchError((e) => this.handleError(e)),);
  }

  public inative(entity: T, pathParams?:{}): Observable<T>
  {
    return this.http.put(`${this.getBaseURL(pathParams)}/${entity.id}/desativar`, null, blockUi()).pipe(
          map(response => this.convertToType(response)),
          catchError((e) => this.handleError(e)),);
  }

  public handleError(responseError: HttpErrorResponse | any)
  {
    this.logger.error('Erro ao realizar requisição no servidor', responseError);
    return observableThrowError(this.errorHandlerService.createErrorApp(responseError));
  }

  public parseErrorBlob(err: HttpErrorResponse): Observable<HttpErrorResponse> 
  {
      const reader: FileReader = new FileReader();

      if (err.error.size == 0)
      {
        const obs = Observable.create((observer: any) => 
        {      
          let newErr = new HttpErrorResponse({ error:{}, headers: err.headers, status: err.status, statusText: err.statusText, url: err.url});
          observer.error(newErr);
          observer.complete();
        });
        return obs;
      }

      const obs = Observable.create((observer: any) => 
      {
        reader.onloadend = (e) => 
        {
          let result:string = <string>reader.result;
          let newErr = new HttpErrorResponse({ error: JSON.parse(result), headers: err.headers, status: err.status, statusText: err.statusText, url: err.url});
          observer.error(newErr);
          observer.complete();
        }
      });
      reader.readAsText(err.error);
      return obs;
  }



  public executeNamedQueryOrderedAndPaged(url: string, numeroPagina: number = 1, tamanhoPagina: number = 20, parametroOrdenacao?: string, sentidoOrdenacao?: SentidoOrdenacao, params?: any, options?: any, pathParams?:{}): Observable<any>
  {
    params = {
      ...params,
      numeroPagina: numeroPagina,
      tamanhoPagina: tamanhoPagina,
    };

    let orderBy: string;
    if (parametroOrdenacao)
    {
        const sentido = sentidoOrdenacao ? sentidoOrdenacao : SentidoOrdenacao.ASCENDENTE;
        orderBy = `${parametroOrdenacao}+${sentido}`;
        params = Object.assign(params, { orderBy: orderBy });
    }

    options = {...options, params};

    BnDateUtils.convertDatesToString(options.params);
    return this.http.get(url, blockUi(options)).pipe(
      retryWhen(progressEventErrorRetryStrategy()), 
      map(
        (response: any) => {
          const pagedResult = Object.assign(new EntityPagedResult<T>(), response);

          if (pagedResult.list) {
            pagedResult.list = pagedResult.list.map(item => this.convertToType(item));
          }

          return pagedResult;
        }
      )
    );
  }
}